import type { ATSJob, ResolvedJobStages, StageMapping } from '@kiku/integrations/types';
import { useQueries } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '~/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '~/components/ui/select';
import { Skeleton } from '~/components/ui/skeleton';
import { api } from '~/lib/api';
import { i18n } from '~/lib/i18n';

const STAGE_FIELDS = [
  'initialStage',
  'passInterviewStage',
  'failInterviewStage',
  'recommendedStage'
] as const;

type StageMappingKey = (typeof STAGE_FIELDS)[number];

type AtsStageSelectorStepProps = {
  selectedAtsJobs: ATSJob[];
  orgId: string;
  onComplete: (selections: Record<string, StageMapping>) => void;
  onBack: () => void;
};

export function AtsStageSelectorStep({
  selectedAtsJobs,
  orgId,
  onComplete,
  onBack
}: AtsStageSelectorStepProps): React.ReactNode {
  const { t } = useTranslation();

  // Track only user overrides; defaults come from query data
  const [userOverrides, setUserOverrides] = useState<
    Record<string, Partial<Record<StageMappingKey, string>>>
  >({});

  const stageQueries = useQueries({
    queries: selectedAtsJobs.map((job) => ({
      queryKey: ['integrations', 'jobStages', orgId, job.id] as const,
      queryFn: async (): Promise<ResolvedJobStages> => {
        const res = await api.api.org[':orgId'].integrations.jobs[':atsJobId'].stages.$get({
          param: { orgId, atsJobId: job.id }
        });
        if (!res.ok) throw new Error(i18n.t('errors.unexpected'));
        return res.json() as Promise<ResolvedJobStages>;
      }
    }))
  });

  // Derive selections: defaults from query data, overridden by user choices
  const selections = useMemo(
    () =>
      Object.fromEntries(
        selectedAtsJobs.map((job, i) => {
          const defaults = stageQueries[i]?.data?.defaults;
          const overrides = userOverrides[job.id] ?? {};
          return [
            job.id,
            {
              initialStage: overrides.initialStage ?? defaults?.initialStage ?? '',
              passInterviewStage:
                overrides.passInterviewStage ?? defaults?.passInterviewStage ?? '',
              failInterviewStage:
                overrides.failInterviewStage ?? defaults?.failInterviewStage ?? '',
              recommendedStage: overrides.recommendedStage ?? defaults?.recommendedStage ?? ''
            } satisfies Record<StageMappingKey, string>
          ];
        })
      ),
    [userOverrides, ...stageQueries.map((q) => q.data)]
  );

  const isLoading = stageQueries.some((q) => q.isLoading);

  const allFilled = selectedAtsJobs.every((job) => {
    const sel = selections[job.id];
    return (
      sel &&
      sel.initialStage.length > 0 &&
      sel.passInterviewStage.length > 0 &&
      sel.failInterviewStage.length > 0 &&
      sel.recommendedStage.length > 0
    );
  });

  function handleFieldChange(jobId: string, field: StageMappingKey, value: string): void {
    setUserOverrides((prev) => ({
      ...prev,
      [jobId]: { ...prev[jobId], [field]: value }
    }));
  }

  function handleContinue(): void {
    const result: Record<string, StageMapping> = {};
    for (const job of selectedAtsJobs) {
      const sel = selections[job.id];
      if (sel) result[job.id] = sel as StageMapping;
    }
    onComplete(result);
  }

  return (
    <div className="flex flex-1 min-h-0 flex-col gap-6">
      <div>
        <h2 className="text-lg font-semibold">{t('wizard.stageSelector.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('wizard.stageSelector.description')}
        </p>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0">
          {selectedAtsJobs.map((job) => (
            <div key={job.id} className="rounded-lg border border-border bg-card p-4">
              <Skeleton className="mb-3 h-4 w-48" />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {STAGE_FIELDS.map((field) => (
                  <div key={field} className="flex flex-col gap-1">
                    <Skeleton className="h-3 w-24" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col gap-4 overflow-y-auto flex-1 min-h-0">
          {selectedAtsJobs.map((job, idx) => {
            const stages = stageQueries[idx]?.data?.stages ?? [];
            const sel = selections[job.id] ?? {
              initialStage: '',
              passInterviewStage: '',
              failInterviewStage: '',
              recommendedStage: ''
            };

            return (
              <div key={job.id} className="rounded-lg border border-border bg-card p-4">
                <p className="mb-3 text-sm font-medium">{job.title}</p>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {STAGE_FIELDS.map((field) => (
                    <div key={field} className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-foreground">
                        {t(`integrations.stageMapping.${field}`)}
                      </label>
                      <Select
                        value={sel[field] || undefined}
                        onValueChange={(value) => {
                          if (value !== null) handleFieldChange(job.id, field, value);
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder={t('integrations.stageMapping.selectStage')}>
                            {stages.find((s) => s.id === sel[field])?.name}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {stages.map((stage) => (
                            <SelectItem key={stage.id} value={stage.id}>
                              {stage.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>
          {t('wizard.back')}
        </Button>
        <Button disabled={!allFilled || isLoading} onClick={handleContinue}>
          {t('wizard.stageSelector.continue')}
        </Button>
      </div>
    </div>
  );
}
